import { useEffect } from 'react';

interface SchemaMarkupProps {
  type: 'LocalBusiness' | 'Service' | 'FAQPage' | 'BreadcrumbList';
  data: any;
}

export default function SchemaMarkup({ type, data }: SchemaMarkupProps) {
  useEffect(() => {
    let schema: any = {
      "@context": "https://schema.org"
    };

    switch (type) {
      case 'LocalBusiness':
        schema = {
          ...schema,
          "@type": "LocalBusiness",
          "name": data.name,
          "description": data.description,
          "url": data.url,
          "telephone": data.telephone,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": data.address.locality,
            "addressRegion": data.address.region,
            "addressCountry": data.address.country
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": data.geo.latitude,
            "longitude": data.geo.longitude
          },
          "openingHours": data.openingHours,
          "priceRange": data.priceRange,
          "areaServed": data.areaServed,
          "serviceType": data.serviceType,
          "hasCredential": data.hasCredential,
          "sameAs": data.sameAs
        };
        break;

      case 'Service':
        schema = {
          ...schema,
          "@type": "Service",
          "name": data.name,
          "description": data.description,
          "provider": {
            "@type": "LocalBusiness",
            "name": data.provider.name,
            "telephone": data.provider.telephone,
            "url": data.provider.url,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": data.provider.address.locality,
              "addressRegion": data.provider.address.region,
              "addressCountry": data.provider.address.country
            }
          },
          "areaServed": data.areaServed,
          "serviceType": data.serviceType,
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "TRY",
            "priceRange": data.priceRange
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": data.catalog.name,
            "itemListElement": data.catalog.services.map((service: any, index: number) => ({
              "@type": "Offer",
              "position": index + 1,
              "itemOffered": {
                "@type": "Service",
                "name": service.name,
                "description": service.description
              }
            }))
          }
        };
        break;

      case 'FAQPage':
        schema = {
          ...schema,
          "@type": "FAQPage",
          "mainEntity": data.faqs.map((faq: any) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        };
        break;

      case 'BreadcrumbList':
        schema = {
          ...schema,
          "@type": "BreadcrumbList",
          "itemListElement": data.breadcrumbs.map((item: any, index: number) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
          }))
        };
        break;
    }

    // Create and inject schema script
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify(schema);
    schemaScript.id = `schema-${type.toLowerCase()}-${Date.now()}`;
    
    document.head.appendChild(schemaScript);

    // Cleanup function
    return () => {
      const existingScript = document.getElementById(schemaScript.id);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);

  return null;
}